import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  useMediaQuery,
  Paper,
  Rating,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Chip,
} from '@mui/material'
import {
  Pool as PoolIcon,
  KingBed as KingBedIcon,
  RoomService as RoomServiceIcon,
  Wifi as WifiIcon,
  LocalParking as LocalParkingIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AcUnit as AcUnitIcon,
  Security as SecurityIcon,
  LocalLaundryService as LaundryIcon,
  LocalCafe as CafeIcon,
  CleaningServices as HousekeepingIcon,
} from '@mui/icons-material'
import { useCurrency } from '../context/CurrencyContext'
import Carousel from 'react-material-ui-carousel'
import roomsData from '../data/rooms.json'
import HeroSection from './sections/HeroSection'
import FeaturesSection from './sections/FeaturesSection'
import FeaturedRooms from './sections/FeaturedRooms'
import Testimonials from './sections/Testimonials'

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
}

const featuredRooms = [
  { 
    title: 'Deluxe Suite', 
    image: '/room-1.jpg', 
    price: 24999, 
    description: 'Spacious suite with city views' 
  },
  { 
    title: 'Executive Room', 
    image: '/room-2.jpg', 
    price: 34999, 
    description: 'Luxury room with premium amenities' 
  },
  { 
    title: 'Presidential Suite', 
    image: '/room-3.jpg', 
    price: 49999, 
    description: 'Ultimate luxury experience' 
  },
];

const carouselItems = [
  {
    image: '/images/rooms/family-suite/room-1.jpeg',
    title: 'Family Suite',
    description: 'Spacious accommodation perfect for family getaways'
  },
  {
    image: '/images/rooms/super-deluxe/room-1.jpeg',
    title: 'Super Deluxe Room',
    description: 'Experience luxury and comfort at its finest'
  },
  {
    image: '/images/rooms/deluxe-ac/room-1.jpeg',
    title: 'Deluxe AC Room',
    description: 'Modern comfort with elegant design'
  }
]

const features = [
  {
    icon: <KingBedIcon sx={{ fontSize: 40 }} />,
    title: 'Premium Rooms',
    description: 'Elegantly furnished rooms with modern amenities and city views'
  },
  {
    icon: <AcUnitIcon sx={{ fontSize: 40 }} />,
    title: 'Climate Control',
    description: 'Individual temperature control in each room for your comfort'
  },
  {
    icon: <WifiIcon sx={{ fontSize: 40 }} />,
    title: 'High-Speed WiFi',
    description: 'Stay connected with complimentary high-speed internet'
  },
  {
    icon: <RoomServiceIcon sx={{ fontSize: 40 }} />,
    title: '24/7 Room Service',
    description: 'Round-the-clock service at your convenience'
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: '24/7 Security',
    description: 'Advanced security systems and professional staff'
  },
  {
    icon: <LocalParkingIcon sx={{ fontSize: 40 }} />,
    title: 'Valet Parking',
    description: 'Convenient and secure parking for our guests'
  },
  {
    icon: <LaundryIcon sx={{ fontSize: 40 }} />,
    title: 'Laundry Service',
    description: 'Professional laundry and dry-cleaning services'
  },
  {
    icon: <HousekeepingIcon sx={{ fontSize: 40 }} />,
    title: 'Daily Housekeeping',
    description: 'Maintaining pristine cleanliness in your room'
  }
]

const rooms = [
  {
    ...roomsData.rooms[0],
    featured: true
  },
  {
    ...roomsData.rooms[2],
    featured: true
  },
  {
    ...roomsData.rooms[3],
    featured: true
  }
]

const testimonials = [
  {
    name: 'Sarah Johnson',
    rating: 5,
    comment: 'An exceptional stay with impeccable service. The attention to detail was remarkable.',
    position: 'Business Executive'
  },
  {
    name: 'David Chen',
    rating: 5,
    comment: 'The epitome of luxury. The rooms and amenities exceeded all expectations.',
    position: 'Travel Blogger'
  },
  {
    name: 'Emma Williams',
    rating: 5,
    comment: 'A truly memorable experience. The staff went above and beyond to ensure our comfort.',
    position: 'Hotel Critic'
  }
]

const Home = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { convertPrice } = useCurrency()
  
  const [heroRef, heroInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })
  
  const [featuresRef, featuresInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [roomsRef, roomsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const handleViewDetails = (room: typeof roomsData.rooms[0]) => {
    console.log('Navigating to room details from home:', room.id)
    navigate(`/rooms/${room.id}`)
  }

  return (
    <Box>
      <HeroSection />
      <FeaturesSection />
      <FeaturedRooms />
      <Testimonials />
    </Box>
  )
}

export default Home 